import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { calculateAge } from "../utils/utils";
import InitialsCircle from "../components/List/InitialsCircle";
import SubjectiveContent from "./patient/SubjectiveContent";
import ObjectiveContent from "./patient/ObjectiveContent";
import AssessmentContent from "./patient/AssessmentContent";
import PlanContent from "./patient/PlanContent";
import { BASE_URL, getAxiosConfig } from "../components/interface/Config";
import LoadingAnimation from "../components/LoadingSpinner";

import TeleconsultHistory from "./patient/TeleconsultHistory";
import { apiHandler } from "../services/authService";

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  gender: "Male" | "Female" | "Other";
  dob: string;
  blood_group: string;
  address: {
    city: string;
    state: string;
    country: string;
    zipcode: string;
    address1: string;
    address2: string;
  };
  hospital_name: string;
  profile_image: string | null;
}

interface ActionButtonProps {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  isSelected: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  text,
  onClick,
  isSelected,
}) => (
  <button
    className={`flex items-center py-2 px-2 rounded-md transition-colors duration-200 ${
      isSelected
        ? "bg-primary text-white"
        : "bg-white text-primary border-2 border-primary"
    }`}
    onClick={onClick}
  >
    {icon}
    {/* Display text only on medium and larger screens */}
    <span className="hidden sm:inline-block ml-2">{text}</span>
  </button>
);

const PatientDetails: React.FC = () => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const navigate = useNavigate();
  const [loadingTab, setLoadingTab] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Teleconsult");
  const token = sessionStorage.getItem("authToken");

  const { refNumber } = useParams();
  console.log("rref", refNumber);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {

        const url        = `${BASE_URL}/resource/users/${refNumber}`;
        const response   = await apiHandler('', 'GET', {}, url);

        if (!response) {
          throw new Error("No response received from the server");
        }

        // const response = await axios.get(
        //   `${BASE_URL}/resource/users/${refNumber}`,
        //   {
        //     headers: { ...getAxiosConfig().headers },
        //   }
        // );

        if (response.status === 200) {
          setPatient(response.data.data.users);
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error fetching patient data:", (error as Error).message);
      }
    };

    fetchPatientData();
  }, [refNumber, token]);

  const getInitials = (firstName: string, lastName: string | null) => {
    if (!firstName) {
      return "";
    }

    const firstInitial = firstName[0] ? firstName[0].toUpperCase() : "";
    const secondInitial = lastName
      ? lastName[0]
        ? lastName[0].toUpperCase()
        : ""
      : firstName[1]
      ? firstName[1].toUpperCase()
      : "";

    return `${firstInitial}${secondInitial}`;
  };

  const initials = getInitials(
    patient?.first_name || "",
    patient?.last_name || ""
  );
  console.log(initials);

  const handleTabChange = (tab: string) => {
    setLoadingTab(true); // Start loading animation
    setSelectedTab(tab); // Change tab

    // Simulate loading content (this should be replaced with actual content fetching if necessary)
    setTimeout(() => {
      setLoadingTab(false); // Stop loading animation after content is ready
    }, 500); // Simulated delay
  };

  return (
    <div className="overflow-hidden h-screen flex flex-col mt-2 bg-white">
      {/* Header with Back button and patient info */}
      <div className="sticky top-0 bg-white z-10 shadow-lg w-full px-4 flex items-center justify-between flex-wrap sm:flex-nowrap">
        {/* Back Button */}
        <button
          className="flex items-center justify-center w-9 h-9 bg-gray-200 text-black rounded-[8px] hover:bg-gray-400 transition duration-300 ease-in-out mb-2 sm:mb-0"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowBackIcon style={{ fontSize: "1rem", color: "black" }} />
        </button>

        {/* Patient Info */}
        <div className="flex-1 flex items-center ml-4 sm:ml-8 mb-2">
          <InitialsCircle
            initials={initials}
            gender={patient?.gender}
            name={""}
          />
          {patient && (
            <div className="ml-4 flex flex-col">
              <p className="flex items-center text-gray-800 text-sm sm:text-base">
                <b>
                  {patient.first_name} {patient.last_name}
                </b>
                <span className="mx-2 text-gray-500">|</span>
                <span className="text-gray-600">
                  {calculateAge(patient.dob)} Yrs
                </span>
                <span className="text-gray-600 ml-1">
                  {patient.gender === "Female"
                    ? "(F)"
                    : patient.gender === "Male"
                    ? "(M)"
                    : "(Other)"}
                </span>
              </p>
              <div className="flex text-sm text-gray-500">
                <span>{patient.mobile}</span>
                <span className="mx-2">|</span>
                <span>{patient.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-4 font-semibold sm:ml-4 flex-wrap sm:flex-nowrap sm:overflow-x-auto w-full sm:w-auto">
          <div className="flex sm:flex-nowrap flex-wrap justify-between w-full sm:w-auto gap-4 sm:gap-2">
            <ActionButton
              icon={<VideoCameraFrontIcon className="h-5 w-5" />}
              text="Teleconsult"
              isSelected={selectedTab === "Teleconsult"}
              onClick={() => handleTabChange("Teleconsult")}
            />
            <ActionButton
              icon={<AssignmentIcon className="h-5 w-5" />}
              text="Subjective"
              isSelected={selectedTab === "subjective"}
              onClick={() => handleTabChange("subjective")}
            />
            <ActionButton
              icon={<AssessmentIcon className="h-5 w-5" />}
              text="Objective"
              isSelected={selectedTab === "objective"}
              onClick={() => handleTabChange("objective")}
            />
            <ActionButton
              icon={<AssessmentIcon className="h-5 w-5" />}
              text="Assessment"
              isSelected={selectedTab === "assessment"}
              onClick={() => handleTabChange("assessment")}
            />
            <ActionButton
              icon={<CheckCircleIcon className="h-5 w-5" />}
              text="Plan"
              isSelected={selectedTab === "plan"}
              onClick={() => handleTabChange("plan")}
            />
          </div>
        </div>
      </div>

      {/* Content area (scrollable part) */}
      <div className="flex-1 overflow-y-auto bg-gray-100">
        {loadingTab ? (
          <div className="flex justify-center items-center h-full bg-[#eaeaea]">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            {selectedTab === "subjective" && (
              <SubjectiveContent
                id={refNumber || ""}
                gender={patient?.gender || "Other"}
              />
            )}
            {selectedTab === "objective" && refNumber && (
              <ObjectiveContent id={refNumber} />
            )}
            {selectedTab === "assessment" && refNumber && (
              <AssessmentContent id={refNumber} />
            )}
            {selectedTab === "plan" && refNumber && (
              <PlanContent id={refNumber} />
            )}
            {selectedTab === "Teleconsult" && refNumber && (
              <TeleconsultHistory patientId={refNumber} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientDetails;
